import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Pencil, Trash, Ellipsis, Car, Settings } from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { toast } from "react-toastify";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { companyAtom } from "@/store/company";
import { PAGE_SIZE } from "@/store";
import { Vehicle } from "@/types/vehicle";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import VehiclesForm from "./VehiclesForm";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { PageHeader } from "@/components/PageHeader";
import VehicleIcon from "@/components/icons/VehicleIcon";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Vehicle[];
}

// Ownership badge variants
const ownershipVariantMap: { [key in Vehicle["ownership"]]: string } = {
  owned: "primary",
  rented: "accent",
};

export const VehiclesList = () => {
  const { t } = useTranslation();
  const { token, user } = useAuth();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState<number | null>(null);
  const [vehicleToDelete, setVehicleToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [company] = useAtom(companyAtom);

  const handleDetails = (vehicleId: number) => {
    navigate(`/vehicles/${vehicleId}`);
  };

  const handleDeleteClick = (vehicleId: number) => {
    setVehicleToDelete(vehicleId);
  };

  const deleteMutation = useMutation({
    mutationFn: async (vehicleId: number) => {
      const response = await fetch(getApiUrl(`vehicles/${vehicleId}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete vehicle");
      }
    },
    onSuccess: () => {
      console.log("Vehicle deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["vehicles", company?.id, currentPage],
      });
      toast.success("Vehicle deleted successfully");
      setVehicleToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setVehicleToDelete(null);
    },
  });

  // Determine if we need to filter by company based on user role
  const isAdmin = user?.rol === "admin";

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ["vehicles", company?.id, currentPage],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;

      const urlParams =
        user?.rol === "admin"
          ? company?.id
            ? `&company=${company.id}`
            : ``
          : company?.id
            ? `&company=${company.id}`
            : `&company=${user?.company?.id}`;

      const response = await fetch(
        getApiUrl(`vehicles/?limit=${PAGE_SIZE}&offset=${offset}${urlParams}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch vehicles");
      }
      return response.json();
    },
  });

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (vehicleId: number) => {
    setEditingVehicleId(vehicleId);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setEditingVehicleId(null);
    setIsDrawerOpen(true);
  };

  if (error) {
    return <div className="text-destructive">Error: {error.message}</div>;
  }

  return (
    <>
      <div>
        {/* <div className="sm:flex sm:items-start sm:justify-between gap-2">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold">{t("vehicles.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("vehicles.description")}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <Button variant="default" onClick={handleAdd}>
              {t("vehicles.add")}
            </Button>
          </div>
        </div> */}

        <PageHeader
          icon={
            <VehicleIcon
              width={180}
              height={180}
              className="w-[50px] h-[50px]"
            />
          }
          actionTitle="vehicles.add"
          title="vehicles.title"
          description="vehicles.description"
          handleNew={handleAdd}
        />

        <div className="px-4">
          <div className="mt-8">
            {isLoading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <div className="p-6">
                      <Skeleton className="h-12 w-12 rounded-full mb-4" />
                      <Skeleton className="h-5 w-[180px] mb-2" />
                      <Skeleton className="h-4 w-[150px] mb-1" />
                      <Skeleton className="h-4 w-[120px] mb-1" />
                      <Skeleton className="h-4 w-[100px]" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {data?.results.map((vehicle) => (
                  <Card
                    key={vehicle.id}
                    className="overflow-hidden hover:cursor-pointer hover:bg-muted"
                    // onClick={() => handleDetails(vehicle.id)}
                    onClick={() => handleEdit(vehicle.id)}
                  >
                    <CardHeader className="relative pb-0">
                      <div className="absolute right-4 top-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <Ellipsis className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleEdit(vehicle.id);
                              }}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              {t("common.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleDeleteClick(vehicle.id);
                              }}
                              className="text-destructive"
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              {t("common.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-full overflow-hidden bg-accent flex items-center justify-center">
                          <Car className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-medium">
                            {vehicle.name || vehicle.license_plate}
                          </h3>
                          <Badge
                            variant={ownershipVariantMap[vehicle.ownership]}
                            className="mt-1"
                          >
                            {vehicle.ownership}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        {t("vehicles.fields.license_plate")}:{" "}
                        {vehicle.license_plate}
                      </p>
                      <p className="text-sm text-muted-foreground mb-1">
                        {t("vehicles.fields.vehicle_type")}:{" "}
                        {vehicle?.vehicle_type?.name ?? "-"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("vehicles.fields.fuel_type")}:{" "}
                        {vehicle?.fuel_type?.name ?? "-"}
                      </p>
                    </CardContent>
                    {isAdmin && vehicle?.company && (
                      <CardFooter className="border-t pt-4">
                        <div className="text-xs text-muted-foreground">
                          {t("vehicles.fields.company")}: {vehicle.company.name}
                        </div>
                      </CardFooter>
                    )}
                  </Card>
                ))}
              </div>
            )}
            {data && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                hasNextPage={!!data.next}
                hasPreviousPage={!!data.previous}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingVehicleId ? t("vehicles.edit") : t("vehicles.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingVehicleId
                ? t("vehicles.edit-description")
                : t("vehicles.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <VehiclesForm
            vehicleId={editingVehicleId || undefined}
            onSuccess={() => setIsDrawerOpen(false)}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>

      {/* Delete confirmation modal */}
      <DeleteModal
        open={vehicleToDelete !== null}
        onOpenChange={(open) => !open && setVehicleToDelete(null)}
        entity={t("vehicles.title", "vehicle").toLowerCase()}
        onDelete={() => deleteMutation.mutate(vehicleToDelete!)}
        isPending={deleteMutation.isPending}
      />
    </>
  );
};
